import { useState } from "react";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
    Plus,
    SquarePen,
    Check,
    X,
} from "lucide-react";
import {
    RuleVersion,
} from "../../shared/schema";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { Popup } from "@/widgets/RuleDetailDailog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { useGetOffers, useIndividualGetOffers, useToggleOfferStatus, useToggleRuleValueStatus } from "@/hooks/useLocalStorage";
import { useBreadcrumbs } from "../BreadcrumbContext";
import { IndividualOfferForm, OfferForm } from "@/shared/partnerschema";
import { AddOffer } from "./AddOffer";
import RulesPopup from "./RulesPopup";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import RulesSetup from "./RulesSetup";
import { useConfirmDialog } from "@/widgets/ConfirmDailog";
import { AddIndividualOffer } from "./AddIndivisualOffer";

interface TableHierarchicalNodeProps {
    topLevelData: any[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (code: number) => void;
    onEditForm: (data: any) => void;
    onViewRules: (data: any) => void;
    onRuleSetup: (data: any) => void;
    setViewType: (data: any) => void
}

function TableForm({
    topLevelData,
    pageIndex,
    pageSize,
    totalCount,
    onPageChange,
    onEditForm,
    onViewRules,
    onRuleSetup,
    setViewType
}: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<IndividualOfferForm[]>([]);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);
    const [showPopup, setShowPopup] = React.useState(false);
    const [dataHead, setDataHead] = React.useState<RuleVersion | undefined>();
    const [dataContent, setDataContent] = React.useState<string | null>("");
    const queryClient = useQueryClient();
    const [popupName, setPopupName] = React.useState("");

    const { confirm, dialog: confirmDialog } = useConfirmDialog();

    const toggleStatus = useToggleOfferStatus();

    React.useEffect(() => {
        if (topLevelData) {
            setCurrentData(topLevelData);
        }
    }, [topLevelData]);

    const onStatusChange = async (data: IndividualOfferForm) => {
        const isCurrentlyActive = data.IsActive;
        const confirmed = await confirm({
            heading: isCurrentlyActive ? "Deactivate Individual Offer" : "Activate Individual Offer",
            statement: isCurrentlyActive
                ? `Are you sure you want to deactivate "${data.ExchangeOfferName}"?`
                : `Are you sure you want to activate "${data.ExchangeOfferName}"?`,
            yesText: isCurrentlyActive ? "Deactivate" : "Activate",
            noText: "Cancel",
        });

        if (!confirmed) return;

        try {
            await toggleStatus.mutateAsync(data.Id!);
            setAlertMessage(
                isCurrentlyActive
                    ? "Individual Offer deactivated successfully."
                    : "Individual Offer activated successfully."
            );
            setReqStatus(true);
            setAlertOpen(true);
        } catch (err: any) {
            setAlertMessage(err.message);
            setReqStatus(false);
            setAlertOpen(true);
        }
    };

    const handleSetupOffer = (data: IndividualOfferForm) => {
        console.log("data", data)
        const payload = JSON.stringify({
            Id: data.Id,
            ExchangeOfferName: data.ExchangeOfferName,
            ExPartnerId: "",
            OfferDetail: data.OfferDetails,
            CreatedDate: data.CreatedDate,
            ModifiedDate: data.ModifiedDate,
            IsActive: data.IsActive,
            ReferenceFormId: data.ReferenceFormId,
        });

        document.cookie = `defaultOffer_setup=${encodeURIComponent(payload)}; domain=.workfinity.com; path=/; max-age=300`;

        window.location.href = "https://form-ai-portal.workfinity.com/defaultOffers";
    };

    const handleAdditionalOffer = (data: IndividualOfferForm) => {
        window.history.pushState(
            {
                partnerData: data,
            },
            "",
            `/additionaloffers/${data.Id}`
        );

        window.dispatchEvent(new PopStateEvent("popstate"));
    };

    return (
        <div>
            {confirmDialog}
            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Id</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Raw Rules</TableHead>
                            <TableHead>Rule Setup</TableHead>
                            <TableHead>Rule Values</TableHead>
                            <TableHead>Create Offer</TableHead>
                            <TableHead>View Offer</TableHead>
                            <TableHead>Additional Offer</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Valid From</TableHead>
                            <TableHead>Valid To</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow key={data.Id}>
                                <TableCell className="">{data.Id}</TableCell>

                                <TableCell className="">{data.ExchangeOfferName}</TableCell>

                                {/* Available Raw Rules — opens RulesPopup */}
                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button
                                        variant="link"
                                        className="max-w-xs truncate"
                                        onClick={() => onViewRules(data)}
                                    >
                                        View
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button
                                        variant="link"
                                        className="max-w-xs truncate"
                                        onClick={() => onRuleSetup(data)}
                                    >
                                        SetUp
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button
                                        variant="link"
                                        className="max-w-xs truncate"
                                        onClick={() => setViewType(data)}
                                    >
                                        View
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button
                                        variant="link"
                                        className="max-w-xs truncate"
                                        onClick={() => handleSetupOffer(data)}
                                    >
                                        Setup Offer
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button
                                        variant="link"
                                        className="max-w-xs truncate"
                                        disabled={!data?.ReferenceFormId}
                                        onClick={() => {
                                            if (data?.ReferenceFormId) {
                                                window.open(
                                                    `https://form-engine.workfinity.com/designer/${data.ReferenceFormId}`,
                                                    "_blank"
                                                );
                                            }
                                        }}
                                    >
                                        View Offer
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button
                                        variant="link"
                                        className="max-w-xs truncate"
                                        onClick={() => handleAdditionalOffer(data)}
                                    >
                                        Setup Additional Offer
                                    </Button>
                                </TableCell>

                                <TableCell>
                                    <span
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                                            ${data.IsActive
                                                ? "bg-green-100 text-green-700"
                                                : "bg-red-100 text-red-600"
                                            }`}
                                    >
                                        {data.IsActive ? (
                                            <>Active</>
                                        ) : (
                                            <>Inactive</>
                                        )}
                                    </span>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <div className="flex flex-col">
                                        {data.ValidFrom ? (
                                            <>
                                                <span>
                                                    {new Date(data.ValidFrom).toLocaleDateString("en-IN", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })}
                                                </span>
                                                <span className="text-xs text-black">
                                                    {new Date(data.ValidFrom).toLocaleTimeString("en-IN", {
                                                        hour: "2-digit", minute: "2-digit",
                                                    })}
                                                </span>
                                            </>
                                        ) : "-"}
                                    </div>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <div className="flex flex-col">
                                        {data.ValidTo ? (
                                            <>
                                                <span>
                                                    {new Date(data.ValidTo).toLocaleDateString("en-IN", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })}
                                                </span>
                                                <span className="text-xs text-black">
                                                    {new Date(data.ValidTo).toLocaleTimeString("en-IN", {
                                                        hour: "2-digit", minute: "2-digit",
                                                    })}
                                                </span>
                                            </>
                                        ) : "-"}
                                    </div>
                                </TableCell>

                                <TableCell>
                                    <div className="flex flex-row items-center space-x-2">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-9 h-9 md:w-10 md:h-10 p-0"
                                                    tooltip="More actions"
                                                >
                                                    <Plus size={20} strokeWidth={2.5} />
                                                    <span className="sr-only">More actions</span>
                                                </Button>
                                            </DropdownMenuTrigger>

                                            <DropdownMenuContent align="end" className="min-w-[180px]">
                                                <DropdownMenuItem
                                                    className="flex items-center px-2 cursor-pointer"
                                                    onClick={() => onEditForm(data)}
                                                >
                                                    <SquarePen size={20} strokeWidth={2.5} className="mr-2" />
                                                    <span className="text-sm">Edit Offer</span>
                                                </DropdownMenuItem>

                                                <DropdownMenuItem
                                                    className="flex items-center px-2 cursor-pointer"
                                                    onClick={() => onStatusChange(data)}
                                                    disabled={toggleStatus.isPending}
                                                >
                                                    {data.IsActive ? (
                                                        <>
                                                            <X size={20} strokeWidth={2.5} className="mr-2 text-red-600" />
                                                            <span className="text-sm">Deactivate Offer</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Check size={20} strokeWidth={2.5} className="mr-2 text-green-600" />
                                                            <span className="text-sm">Activate Offer</span>
                                                        </>
                                                    )}
                                                </DropdownMenuItem>

                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <div className="mt-2">
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                </div>
            </div>

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />

            {showPopup && (
                <Popup
                    popupName={popupName}
                    heading={dataHead}
                    content={dataContent}
                    onClose={() => setShowPopup(false)}
                />
            )}

        </div>
    );
}


export function IndivisualOffer() {
    const { setBreadcrumbs } = useBreadcrumbs();
    const [topLevelTypes, setTopLevelTypes] = useState<IndividualOfferForm[]>([]);

    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [editFormData, setEditFormData] = useState<any>(null);

    const [rulesPopupOpen, setRulesPopupOpen] = useState(false);
    const [selectedOfferForRules, setSelectedOfferForRules] = useState<IndividualOfferForm | null>(null);

    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [ruleSetupOpen, setRuleSetupOpen] = useState(false);
    const [selectedOfferForSetup, setSelectedOfferForSetup] = useState<IndividualOfferForm | null>(null);
    const [viewType, setViewType] = useState(false);

    const { data: types, isLoading } = useIndividualGetOffers(pageIndex, pageSize);

    React.useEffect(() => {
        setBreadcrumbs([
            {
                label: "Partners",
                path: "/",
            },
        ]);
    }, []);
console.log("types", types)
    React.useEffect(() => {
        setTopLevelTypes(types?.data?.items ?? []);
        setTotalCount(types?.data?.total ?? 0);
    }, [types]);

    const handleAddForm = () => {
        setEditFormData(null);
        setOpenCreateDialog(true);
    };

    const handleEditForm = (data: any) => {
        setEditFormData(data);
        setOpenCreateDialog(true);
    };

    const handleViewRules = (data: IndividualOfferForm) => {
        setSelectedOfferForRules(data);
        setRulesPopupOpen(true);
    };

    const handleRuleSetup = (data: IndividualOfferForm) => {
        setSelectedOfferForSetup(data);
        setRuleSetupOpen(true);
        setViewType(false)
    };

    const handleRuleView = (data: IndividualOfferForm) => {
        setSelectedOfferForSetup(data);
        setRuleSetupOpen(true);
        setViewType(true)
    };

    return (
        <div className="min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-lg md:text-xl font-semibold text-black">
                            Manage Individual Offers
                        </h2>
                        <p className="text-black mt-1 text-sm md:text-base">
                            Create and Setup Individual Offers
                        </p>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={handleAddForm}
                        >
                            <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                            Add Individual Offer
                        </Button>
                    </div>
                </div>
            </div>

            {!isLoading ? (
                <Card>
                    <CardContent className="p-4 md:p-6">
                        <div className="space-y-2">
                            {topLevelTypes && topLevelTypes.length > 0 ? (
                                <TableForm
                                    topLevelData={topLevelTypes}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                    onEditForm={handleEditForm}
                                    onViewRules={handleViewRules}
                                    onRuleSetup={handleRuleSetup}
                                    setViewType={handleRuleView}
                                />
                            ) : (
                                <div className="text-center py-8 text-black">
                                    <p>No Individual Offers found. Create one to get started.</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="p-6 text-black">Loading...</div>
            )}

            <AddIndividualOffer
                open={openCreateDialog}
                onOpenChange={setOpenCreateDialog}
                initialData={editFormData}
            />

            {rulesPopupOpen &&
                <RulesPopup
                    open={rulesPopupOpen}
                    onClose={() => {
                        setRulesPopupOpen(false);
                        setSelectedOfferForRules(null);
                    }}
                    offerData={selectedOfferForRules ?? undefined}
                />
            }

            {ruleSetupOpen && (
                <RulesSetup
                    open={ruleSetupOpen}
                    onClose={() => { setRuleSetupOpen(false); setSelectedOfferForSetup(null); }}
                    offerData={selectedOfferForSetup ?? undefined}
                    viewType={viewType}
                />
            )}
        </div>
    );
}